import React, { useState, useEffect } from 'react';
import { get, post, put, del } from 'aws-amplify/api';

const Admin = ({ user }) => {
  const [activeTab, setActiveTab] = useState('members');
  const [members, setMembers] = useState([]);
  const [emailForm, setEmailForm] = useState({
    recipients: 'all',
    classYears: '',
    states: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  // Check if user is admin
  const isAdmin = user?.signInUserSession?.accessToken?.payload?.['cognito:groups']?.includes('Admins');

  useEffect(() => {
    if (isAdmin) {
      fetchMembers();
    }
  }, [isAdmin]);

  const fetchMembers = async () => {
    try {
      const response = await get({
        apiName: 'navy-football-api',
        path: '/members'
      }).response;
      
      const membersData = await response.body.json();
      setMembers(membersData);
    } catch (error) {
      console.error('Error fetching members:', error);
    }
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      const emailData = {
        action: 'send_email',
        send_to: emailForm.recipients,
        subject: emailForm.subject,
        message: emailForm.message
      };

      if (emailForm.recipients === 'class_years') {
        emailData.class_years = emailForm.classYears.split(',').map(y => y.trim());
      } else if (emailForm.recipients === 'states') {
        emailData.states = emailForm.states.split(',').map(s => s.trim());
      }

      await post({
        apiName: 'navy-football-api',
        path: '/admin',
        options: { body: emailData }
      }).response;

      setMessage('Email sent successfully!');
      setEmailForm({
        recipients: 'all',
        classYears: '',
        states: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      setMessage('Error sending email. Please try again.');
      console.error('Error sending email:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePromoteAdmin = async (username) => {
    setLoading(true);
    setMessage('');

    try {
      await post({
        apiName: 'navy-football-api',
        path: '/admin',
        options: {
          body: {
            action: 'promote_admin',
            username: username
          }
        }
      }).response;

      setMessage(`${username} promoted to admin successfully!`);
      fetchMembers();
    } catch (error) {
      setMessage('Error promoting user. Please try again.');
      console.error('Error promoting user:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMember = async (username) => {
    if (!window.confirm(`Are you sure you want to delete ${username}?`)) {
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      await del({
        apiName: 'navy-football-api',
        path: '/admin/members',
        options: {
          body: { username: username }
        }
      }).response;

      setMessage(`${username} deleted successfully!`);
      fetchMembers();
    } catch (error) {
      setMessage('Error deleting member. Please try again.');
      console.error('Error deleting member:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-red-100 dark:bg-red-900 p-8 rounded-lg text-center">
          <h2 className="text-2xl font-bold text-red-700 dark:text-red-300 mb-4">
            Access Denied
          </h2>
          <p className="text-red-600 dark:text-red-400">
            You do not have admin privileges to access this page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-md">
        <h2 className="text-2xl font-bold text-navy-primary dark:text-gold-primary mb-6">
          Admin Panel
        </h2>

        {message && (
          <div className={`p-4 rounded-lg mb-6 ${
            message.includes('Error') 
              ? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300' 
              : 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
          }`}>
            {message}
          </div>
        )}

        {/* Tabs */}
        <div className="flex space-x-4 mb-6 border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setActiveTab('members')}
            className={`py-2 px-4 font-medium ${
              activeTab === 'members'
                ? 'text-navy-primary dark:text-gold-primary border-b-2 border-navy-primary dark:border-gold-primary'
                : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            Manage Members
          </button>
          <button
            onClick={() => setActiveTab('email')}
            className={`py-2 px-4 font-medium ${
              activeTab === 'email'
                ? 'text-navy-primary dark:text-gold-primary border-b-2 border-navy-primary dark:border-gold-primary'
                : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            Send Email
          </button>
        </div>

        {/* Members Management Tab */}
        {activeTab === 'members' && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold mb-4">Member Management</h3>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-gray-300 dark:border-gray-600">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-700">
                    <th className="border border-gray-300 dark:border-gray-600 p-3 text-left">Name</th>
                    <th className="border border-gray-300 dark:border-gray-600 p-3 text-left">Email</th>
                    <th className="border border-gray-300 dark:border-gray-600 p-3 text-left">Class Year</th>
                    <th className="border border-gray-300 dark:border-gray-600 p-3 text-left">Role</th>
                    <th className="border border-gray-300 dark:border-gray-600 p-3 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((member, index) => (
                    <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="border border-gray-300 dark:border-gray-600 p-3">
                        {member.first_name} {member.last_name}
                      </td>
                      <td className="border border-gray-300 dark:border-gray-600 p-3">
                        {member.email}
                      </td>
                      <td className="border border-gray-300 dark:border-gray-600 p-3">
                        {member.class_year}
                      </td>
                      <td className="border border-gray-300 dark:border-gray-600 p-3">
                        {member.role || 'Member'}
                      </td>
                      <td className="border border-gray-300 dark:border-gray-600 p-3">
                        <div className="flex space-x-2">
                          {member.role !== 'admin' && (
                            <button
                              onClick={() => handlePromoteAdmin(member.username)}
                              disabled={loading}
                              className="bg-blue-500 text-white px-3 py-1 rounded text-sm hover:bg-blue-600 disabled:opacity-50"
                            >
                              Promote
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteMember(member.username)}
                            disabled={loading}
                            className="bg-red-500 text-white px-3 py-1 rounded text-sm hover:bg-red-600 disabled:opacity-50"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Email Tab */}
        {activeTab === 'email' && (
          <form onSubmit={handleSendEmail} className="space-y-6">
            <h3 className="text-lg font-semibold mb-4">Send Email to Members</h3>
            
            <div>
              <label className="block text-sm font-medium mb-2">Recipients</label>
              <select
                value={emailForm.recipients}
                onChange={(e) => setEmailForm(prev => ({ ...prev, recipients: e.target.value }))}
                className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
              >
                <option value="all">All Members</option>
                <option value="class_years">Specific Class Years</option>
                <option value="states">Specific States</option>
              </select>
            </div>

            {emailForm.recipients === 'class_years' && (
              <div>
                <label className="block text-sm font-medium mb-2">Class Years (comma-separated)</label>
                <input
                  type="text"
                  value={emailForm.classYears}
                  onChange={(e) => setEmailForm(prev => ({ ...prev, classYears: e.target.value }))}
                  placeholder="e.g., 2010, 2011, 2012"
                  className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                />
              </div>
            )}

            {emailForm.recipients === 'states' && (
              <div>
                <label className="block text-sm font-medium mb-2">States (comma-separated)</label>
                <input
                  type="text"
                  value={emailForm.states}
                  onChange={(e) => setEmailForm(prev => ({ ...prev, states: e.target.value }))}
                  placeholder="e.g., VA, MD, CA"
                  className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-2">Subject</label>
              <input
                type="text"
                value={emailForm.subject}
                onChange={(e) => setEmailForm(prev => ({ ...prev, subject: e.target.value }))}
                className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Message</label>
              <textarea
                value={emailForm.message}
                onChange={(e) => setEmailForm(prev => ({ ...prev, message: e.target.value }))}
                rows="6"
                className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                required
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-navy-primary text-white py-3 px-6 rounded-lg hover:bg-navy-secondary transition-colors disabled:opacity-50"
            >
              {loading ? 'Sending...' : 'Send Email'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default Admin;